import React, { useState } from 'react';
import { api, ApiError } from './api';
import { Modal } from './Modal';
import { useToast } from './Toast';

const clean = (v: string) => v.replace(/[^A-Za-z0-9]/g, '').slice(0, 8);

/** Rules shown live while the person types their own code. */
function rules(code: string): { ok: boolean; text: string }[] {
  return [
    { ok: code.length === 8, text: 'Exactly 8 letters or digits' },
    { ok: /[A-Z]/.test(code), text: 'A capital letter' },
    { ok: /[a-z]/.test(code), text: 'A small letter' },
    { ok: /[0-9]/.test(code), text: 'A digit' },
    { ok: code.length > 0 && !/(.)\1\1/.test(code), text: 'No character 3 times in a row' },
  ];
}

export function ChangeCodeDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [cur, setCur] = useState('');
  const [neu, setNeu] = useState('');
  const [again, setAgain] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const list = rules(neu);
  const allOk = list.every((r) => r.ok);
  const same = neu === again && again.length > 0;
  const ready = cur.length === 8 && allOk && same && !busy;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setErr('');
    try {
      await api.changeMyCode(cur, neu);
      toast('Your code was changed. Use the new code the next time you sign in.', 'ok');
      onClose();
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Could not change the code.');
    } finally {
      setBusy(false);
    }
  };

  const type = show ? 'text' : 'password';
  const common = { autoCapitalize: 'off', autoCorrect: 'off', spellCheck: false, autoComplete: 'off', maxLength: 8, className: 'code-input' } as const;

  return (
    <Modal title="Change my code" onClose={onClose}>
      <form onSubmit={submit}>
        <label htmlFor="cc-cur">Current code</label>
        <input id="cc-cur" type={type} value={cur} onChange={(e) => setCur(clean(e.target.value))} {...common} autoFocus />

        <label htmlFor="cc-new">New code (your own choice)</label>
        <input id="cc-new" type={type} value={neu} onChange={(e) => setNeu(clean(e.target.value))} {...common} placeholder="e.g. Sun7Rise" />
        <ul className="rules" aria-label="Rules for the new code">
          {list.map((r) => (
            <li key={r.text} className={r.ok ? 'ok' : ''}>
              <span aria-hidden="true">{r.ok ? '✓' : '○'}</span> {r.text}
            </li>
          ))}
        </ul>

        <label htmlFor="cc-again">Type the new code again</label>
        <input id="cc-again" type={type} value={again} onChange={(e) => setAgain(clean(e.target.value))} {...common} />
        {again.length > 0 && !same && <p className="error">The two new codes are not the same.</p>}

        <label className="check">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} /> Show the codes
        </label>
        <p className="hint">Capital and small letters matter. Choose something you will remember but others cannot guess. If the code is already taken, you will be asked to pick another.</p>
        {err && <p className="error" role="alert">{err}</p>}
        <button className="btn primary block" type="submit" disabled={!ready}>
          {busy ? 'Saving…' : 'Save my new code'}
        </button>
      </form>
    </Modal>
  );
}
